/**
 * Link da Apresentação (migration 258): o vendedor grava o estudo já calculado
 * e manda /#/estudo/<id> ao cliente, que abre sem login.
 *
 * A regra de gravação (saneamento, validação, forma da linha) mora em
 * db/supabase/functions/_shared/apresentacao/link.ts, que a North IA também
 * usa ao gerar o link pelo chat. Aqui ficam as chamadas ao banco do navegador.
 */
import { supabase } from '../providers/supabase/supabase';
import type { Json } from '@/types/database.generated';
import type { Estudo } from './estudo';
import type { UserInfo } from './proposal-utils';
import {
  especialistaDe,
  estudoValido,
  linhaDoLink,
  sanearJson,
  urlDoEstudo as urlComOrigem,
} from '../../../../../db/supabase/functions/_shared/apresentacao/link.ts';

export { DIAS_DO_LINK, estudoValido, sanearJson } from '../../../../../db/supabase/functions/_shared/apresentacao/link.ts';

export type EstudoPublico = {
  estudo: Estudo;
  especialista: UserInfo;
  expiraEm: string;
};

/** O app usa rota por hash (react-admin): o endereço público é /#/estudo/<id>. */
export const urlDoEstudo = (id: string, origin = window.location.origin) => urlComOrigem(id, origin);

/** Grava o estudo e devolve o endereço para o cliente. */
export const criarLinkEstudo = async (estudo: Estudo, userInfo: UserInfo): Promise<{ id: string; url: string; expiraEm: string }> => {
  const linha = linhaDoLink(estudo, userInfo);
  const { data, error } = await supabase
    .from('estudo_links')
    .insert({ cliente: linha.cliente, estudo: linha.estudo as Json, especialista: linha.especialista as Json })
    .select('id, expira_em')
    .single();
  if (error || !data) throw new Error(error?.message ?? 'Link não criado');
  return { id: data.id, url: urlDoEstudo(data.id), expiraEm: data.expira_em };
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Estudo do link; null se não existe, venceu ou não tem a forma esperada. */
export const carregarEstudoPublico = async (id: string): Promise<EstudoPublico | null> => {
  if (!UUID.test(id)) return null;
  const { data, error } = await supabase.rpc('estudo_publico', { p_id: id });
  if (error) throw new Error(error.message);
  const r = data as { estudo?: unknown; especialista?: unknown; expira_em?: unknown } | null;
  if (!r) return null;
  const estudo = sanearJson(r.estudo);
  if (!estudoValido(estudo)) return null;
  return {
    estudo,
    especialista: especialistaDe(sanearJson(r.especialista)),
    expiraEm: typeof r.expira_em === 'string' ? r.expira_em : '',
  };
};
